import {
    type DetailedHTMLProps,
    forwardRef,
    type InputHTMLAttributes,
    ReactNode,
    Ref,
    useId,
    useState,
} from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { GetInputClassName } from '../../_utils/get-input-class-name';
import { prepareLabel } from '../../_utils/prepare-label';
import {
    buildRhfRules,
    type FieldValidate,
    mergeRefs,
    warnAboutRegisterSpread,
} from '../../_utils/rhf-ref';
import { Button } from '../Button/Button';
import {
    InputWrapper,
    InputWrapperSharedProps,
} from '../InputWrapper/InputWrapper';

type HTMLProps = DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
>;

export type FileInputProps = HTMLProps &
    InputWrapperSharedProps & {
        /**
         * The input field name
         */
        name: string;
        /**
         * Input field placeholder
         */
        placeholder?: string;
        /**
         * Input field value (this input is controlled only for now)
         */
        value?: string;
        /**
         * Additional custom styles (overrides)
         */
        disabled?: boolean;
        /**
         * Define if label should be hidden
         */
        invalid?: boolean;
        /**
         * Icon to be displayed at the end of the input
         */
        suffixIcon?: ReactNode;
        /**
         * Debounce value change, it is used only by controlled component
         */
        debounce?: number;
        /**
         * Icon to be displayed at the start of the input
         */
        icon?: ReactNode;
        /**
         * Label for the button that triggers the file input
         */
        buttonLabel?: string;
        /**
         * Custom react-hook-form validation function
         */
        validate?: FieldValidate;
    };

function FileInputComponent(
    props: FileInputProps,
    ref: Ref<HTMLInputElement>,
): ReactNode {
    const {
        name,
        icon,
        className: wrapperClassName,
        required,
        onFocus,
        label: labelProps,
        onBlur,
        buttonLabel,
        labelHidden: __,
        labelClassName: ___,
        horizontal: ____,
        id: inputIdProp,
        value: controlledValue,
        validate,
        'data-testid': dataTestId,
        ...restProps
    } = props;

    const testId = dataTestId || name;
    const generatedInputId = useId();
    const inputId = inputIdProp ?? `file-input-${generatedInputId}`;

    const formContext = useFormContext();

    const [focus, setFocus] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const isRHFControlled = Boolean(
        formContext && name && typeof props.value === 'undefined',
    );
    warnAboutRegisterSpread({
        componentName: 'FileInput',
        isRHFControlled,
        onChange: restProps.onChange,
        ref,
    });

    const label =
        typeof labelProps === 'string'
            ? prepareLabel(name, labelProps, required)
            : labelProps;

    const suffixIconTemplate = props.suffixIcon ? (
        <span className="p-inputgroup-addon">{props.suffixIcon}</span>
    ) : null;

    if (!isRHFControlled) {
        const selectedLabel = selectedFiles?.length
            ? Array.from(selectedFiles)
                  .map((file) => file.name)
                  .join(', ')
            : controlledValue;
        const onClick = (): void => {
            const input = document.getElementById(inputId) as HTMLInputElement;
            if (selectedFiles?.length) {
                input.value = '';
                setSelectedFiles(null);
                return;
            }

            input?.focus();
            input?.click();
        };

        return (
            <InputWrapper
                focus={focus}
                wrapperClassName={wrapperClassName}
                label={label}
                {...props}
            >
                <div className="p-inputgroup flex-1">
                    <Button
                        className="flex-1"
                        onClick={onClick}
                        severity="secondary"
                        label={selectedLabel ?? buttonLabel}
                        icon={icon}
                        iconPos="left"
                        disabled={restProps.disabled}
                    />
                    <input
                        {...restProps}
                        id={inputId}
                        type="file"
                        className={GetInputClassName({
                            basicClassName: 'hidden',
                            invalidState: Boolean(props.invalid),
                        })}
                        multiple={false}
                        ref={ref}
                        name={name}
                        onFocusCapture={() => setFocus(true)}
                        onBlurCapture={() => setFocus(false)}
                        onFocus={(event) => {
                            setFocus(true);
                            onFocus?.(event);
                        }}
                        onBlur={(event) => {
                            onBlur?.(event);
                        }}
                        onChange={(event) => {
                            setSelectedFiles(event.target.files);
                            restProps.onChange?.(event);
                        }}
                    />
                    {suffixIconTemplate}
                </div>
            </InputWrapper>
        );
    }

    return (
        <Controller
            name={name}
            control={formContext!.control}
            rules={buildRhfRules({ required, validate })}
            render={({ field, fieldState }) => {
                const selectedLabel = field.value?.length
                    ? Array.from(field.value as FileList)
                          .map((file) => file.name)
                          .join(', ')
                    : undefined;

                const onClick = (): void => {
                    const input = document.getElementById(
                        inputId,
                    ) as HTMLInputElement;
                    if (field.value?.length) {
                        input.value = '';
                        field.onChange(null);
                        return;
                    }
                    input?.focus();
                    input?.click();
                };
                return (
                    <InputWrapper
                        field={field}
                        fieldState={fieldState}
                        focus={focus}
                        wrapperClassName={wrapperClassName}
                        label={label}
                        {...props}
                    >
                        <div className="p-inputgroup flex-1">
                            <Button
                                className="flex-1"
                                onClick={onClick}
                                severity="secondary"
                                label={selectedLabel ?? buttonLabel}
                                icon={icon}
                                iconPos="left"
                                disabled={restProps.disabled}
                            />
                            <input
                                id={inputId}
                                data-testid={testId}
                                type="file"
                                className={GetInputClassName({
                                    basicClassName: 'hidden',
                                    invalidState: fieldState.invalid,
                                })}
                                multiple={false}
                                ref={mergeRefs(ref, field.ref)}
                                name={field.name}
                                disabled={restProps.disabled}
                                onFocusCapture={() => setFocus(true)}
                                onBlurCapture={() => setFocus(false)}
                                onFocus={(e) => {
                                    setFocus(true);
                                    if (onFocus) {
                                        onFocus(e);
                                    }
                                }}
                                onBlur={(e) => {
                                    field.onBlur();

                                    if (onBlur) {
                                        onBlur(e);
                                    }
                                }}
                                onChange={(e) => {
                                    const files = e.target.files;
                                    if (restProps.onChange) {
                                        restProps.onChange(e);
                                    }
                                    field.onChange(files);
                                }}
                            />
                            {suffixIconTemplate}
                        </div>
                    </InputWrapper>
                );
            }}
        />
    );
}

export const FileInput = forwardRef(FileInputComponent);
